import React, { useRef, useState } from 'react'
import FormField from '../components/FormField'
import GradientButton from '../components/GradientButton'
import IconButton from '../components/IconButton'
import { useNavigate } from 'react-router-dom'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { buildFarmMetadata, type WizardFarm } from '../utils/farmWizard'
import { upsertFarm, type FarmMutationResult } from '../utils/farmApi'
import { logEvent } from '../utils/log'
import { generateUuid } from '../utils/uuid'
import { getBrowserTimeZone, isValidTimeZone } from '../utils/timeBucketing'
import type { EntityMutationSyncState } from '../utils/entityMutationSync'
import { useT, type TranslationKey } from '../i18n'
import type { FacilityType } from '../types/farmWizard'

const POST_CREATE_STATUS_VISIBILITY_MS = 900

const getFarmCreateStatusKey = (state: EntityMutationSyncState): TranslationKey => {
  if (state === 'synced') return 'newFarm.status.createdSynced'
  if (state === 'offline') return 'newFarm.status.createdOffline'
  return 'newFarm.status.createdLocal'
}

export default function NewFarm(){
  const t = useT()
  const principal = useStoredPrincipal()
  const canManageFarms = !principal || principal.role === 'editor' || principal.role === 'admin'

  const [name, setName] = useState('')
  const [facilityType, setFacilityType] = useState<FacilityType>('farm')
  const [classification, setClassification] = useState('')
  const [classificationOther, setClassificationOther] = useState('')
  const [geography, setGeography] = useState('')
  const [geographyOther, setGeographyOther] = useState('')
  const [altitude, setAltitude] = useState<number>(0)
  const [timeZone, setTimeZone] = useState<string>(() => getBrowserTimeZone())
  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState<{ tone: 'info' | 'success'; message: string } | null>(null)
  const saveStatusVersionRef = useRef(0)
  const navigate = useNavigate()

  const classificationOptions = [
    { value: 'Extensive', label: t('newFarm.classification.extensive') },
    { value: 'Semi-Intensive', label: t('newFarm.classification.semiIntensive') },
    { value: 'Intensive', label: t('newFarm.classification.intensive') },
    { value: 'Other', label: t('newFarm.classification.other') }
  ]

  const geographyOptions = [
    { value: 'Lake', label: t('newFarm.geography.lake') },
    { value: 'River', label: t('newFarm.geography.river') },
    { value: 'Ocean', label: t('newFarm.geography.ocean') },
    { value: 'Estuary', label: t('newFarm.geography.estuary') },
    { value: 'Inland-seawater', label: t('newFarm.geography.inlandSeawater') },
    { value: 'Inland-freshwater', label: t('newFarm.geography.inlandFreshwater') },
    { value: 'Other', label: t('newFarm.geography.other') }
  ]

  const facilityTypeOptions = [
    { value: 'farm', label: t('newFarm.facilityType.farm') },
    { value: 'processing-plant', label: t('newFarm.facilityType.processingPlant') }
  ]

  const isProcessingPlant = facilityType === 'processing-plant'

  const applyFarmSaveStatus = (result: Pick<FarmMutationResult, 'syncState' | 'syncPromise'>) => {
    const nextVersion = saveStatusVersionRef.current + 1
    saveStatusVersionRef.current = nextVersion
    setSaveStatus({
      tone: result.syncState === 'synced' ? 'success' : 'info',
      message: t(getFarmCreateStatusKey(result.syncState))
    })

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (saveStatusVersionRef.current !== nextVersion) return
      if (nextState === result.syncState) return
      setSaveStatus({
        tone: nextState === 'synced' ? 'success' : 'info',
        message: t(getFarmCreateStatusKey(nextState))
      })
    })
  }

  async function onSubmit(e: React.FormEvent){
    e.preventDefault()
    if (!canManageFarms) {
      alert(t('newFarm.alert.readOnlyCreate'))
      return
    }
    if(!name.trim()) return alert(t('newFarm.alert.enterName'))

    const trimmedTimeZone = timeZone.trim()
    if (!trimmedTimeZone) return alert(t('newFarm.alert.enterTimeZone'))
    if (!isValidTimeZone(trimmedTimeZone)) {
      return alert(t('newFarm.alert.invalidTimeZone'))
    }

    setLoading(true)
    setSaveStatus(null)
    
    let finalClassification: string | undefined
    if (!isProcessingPlant) {
      finalClassification = classification === 'Other' ? classificationOther.trim() : classification
    }

    let finalGeography: string | undefined
    if (!isProcessingPlant) {
      finalGeography = geography === 'Other' ? geographyOther.trim() : geography
    }

    const metadata = buildFarmMetadata(null, {
      facilityType,
      classification: finalClassification,
      geography: finalGeography
    })
    metadata.timezone = trimmedTimeZone

    const farm: WizardFarm = { 
      id: generateUuid(), 
      name: name.trim(), 
      facilityType,
      classification: finalClassification,
      geography: finalGeography,
      altitude: altitude,
      productionUnits: [],
      metadata,
      createdAt: new Date().toISOString() 
    }

    try {
      const result = await upsertFarm(farm)
      applyFarmSaveStatus(result)
      logEvent('action', 'farm_created', {
        id: farm.id,
        name: farm.name,
        facilityType: farm.facilityType,
        classification: farm.classification,
        geography: farm.geography,
        metadata: farm.metadata
      })
      await new Promise((resolve) => setTimeout(resolve, POST_CREATE_STATUS_VISIBILITY_MS))
      setLoading(false)
      navigate(isProcessingPlant ? `/edit-farm?facilityId=${encodeURIComponent(farm.id)}` : '/type-culture')
    } catch (error) {
      console.warn('NewFarm: failed to create farm', error)
      setLoading(false)
      alert(t('newFarm.alert.saveFailed'))
    }
  }

  const containerStyle: React.CSSProperties = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: 'Inter, system-ui, sans-serif'
  }

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px'
  }

  const titleStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }

  const subtitleStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#6b7280',
    lineHeight: '1.6'
  }

  const formCardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    padding: '32px'
  }

  const progressStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
    padding: '12px 16px',
    background: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd'
  }

  const progressTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#0369a1',
    fontWeight: '500'
  }

  const formGridStyle: React.CSSProperties = {
    display: 'grid',
    gap: '24px'
  }

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '32px'
  }

  const saveStatusStyle = (tone: 'info' | 'success'): React.CSSProperties => ({
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: '10px',
    border: tone === 'success' ? '1px solid #86efac' : '1px solid #bae6fd',
    background: tone === 'success' ? '#f0fdf4' : '#f0f9ff',
    color: tone === 'success' ? '#166534' : '#075985',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '1.5'
  })

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>
          🏠 {t('newFarm.title')}
        </h2>
        <p style={subtitleStyle}>
          {t('newFarm.subtitle')}
        </p>
      </div>

      {/* Progress Indicator */}
      <div style={progressStyle}>
        <span style={{ fontSize: '16px' }}>📍</span>
        <span style={progressTextStyle}>
          {t('newFarm.progress.step', { current: 1, total: 5, label: t('newFarm.progress.step1Label') })}
        </span>
      </div>

      {/* Form Card */}
      <div style={formCardStyle}>
        <form onSubmit={onSubmit} style={formGridStyle}>
          {/* Farm Name */}
          <FormField 
            id="farm-name" 
            label={t('newFarm.field.name.label')} 
            value={name} 
            onChange={(value) => setName(String(value))} 
            placeholder={t('newFarm.field.name.placeholder')} 
            icon="home"
            required
            hint={t('newFarm.field.name.hint')}
          />

          <FormField
            id="facility-type"
            label={t('newFarm.field.facilityType.label')}
            type="select"
            value={facilityType}
            onChange={(value) => setFacilityType((String(value) === 'processing-plant' ? 'processing-plant' : 'farm'))}
            options={facilityTypeOptions}
            icon="dashboard-chart"
            required
            hint={t('newFarm.field.facilityType.hint')}
          />

          {/* Classification */}
          {!isProcessingPlant && (
            <FormField
              id="classification"
              label={t('newFarm.field.classification.label')}
              type="select"
              value={classification}
              onChange={(value) => setClassification(String(value))}
              options={classificationOptions}
              icon="dashboard-chart"
              required
              hint={t('newFarm.field.classification.hint')}
            />
          )}

          {/* Custom Classification */}
          {!isProcessingPlant && classification === 'Other' && (
            <FormField
              id="classification-other"
              label={t('newFarm.field.classification.other.label')}
              value={classificationOther}
              onChange={(value) => setClassificationOther(String(value))}
              placeholder={t('newFarm.field.classification.other.placeholder')}
              icon="settings"
              required
              hint={t('newFarm.field.classification.other.hint')}
            />
          )}

          {/* Geography */}
          {!isProcessingPlant && (
            <FormField
              id="geography"
              label={t('newFarm.field.geography.label')}
              type="select"
              value={geography}
              onChange={(value) => setGeography(String(value))}
              options={geographyOptions}
              icon="water"
              required
              hint={t('newFarm.field.geography.hint')}
            />
          )}

          {/* Custom Geography */}
          {!isProcessingPlant && geography === 'Other' && (
            <FormField
              id="geography-other"
              label={t('newFarm.field.geography.other.label')}
              value={geographyOther}
              onChange={(value) => setGeographyOther(String(value))}
              placeholder={t('newFarm.field.geography.other.placeholder')}
              icon="water"
              required
              hint={t('newFarm.field.geography.other.hint')}
            />
          )}

          {/* Altitude */}
          <FormField
            id="altitude"
            label={t('newFarm.field.altitude.label')}
            type="number"
            value={altitude}
            onChange={(value) => setAltitude(Number(value) || 0)}
            placeholder="0"
            icon="biomas"
            hint={t('newFarm.field.altitude.hint')}
          />

          {/* Time zone */}
          <FormField
            id="time-zone"
            label={t('newFarm.field.timeZone.label')}
            value={timeZone}
            onChange={(value) => setTimeZone(String(value))}
            placeholder={t('newFarm.field.timeZone.placeholder')}
            icon="settings"
            required
            error={timeZone.trim().length > 0 && !isValidTimeZone(timeZone.trim()) ? t('newFarm.field.timeZone.errorInvalid') : undefined}
            hint={t('newFarm.field.timeZone.hint')}
          />

          {/* Action Buttons */}
          <div style={buttonGroupStyle}>
            <GradientButton 
              type="submit" 
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={
                !canManageFarms ||
                !name.trim() ||
                !timeZone.trim() ||
                !isValidTimeZone(timeZone.trim()) ||
                (!isProcessingPlant && (!classification || !geography))
              }
              icon="home"
            >
              {loading ? t('newFarm.button.creating') : t('newFarm.button.createContinue')}
            </GradientButton>
          </div>

          {saveStatus && (
            <div aria-live="polite" role="status" style={saveStatusStyle(saveStatus.tone)}>
              {saveStatus.message}
            </div>
          )}

          <div style={buttonGroupStyle}>
            <GradientButton 
              type="button"
              variant="secondary"
              size="md"
              onClick={() => navigate('/')}
              icon="home"
            >
              {t('newFarm.button.backHome')}
            </GradientButton>
            
            <div style={{ marginLeft: 'auto' }}>
              <IconButton
                icon="chat"
                variant="ghost"
                size="md"
                onClick={() => alert(t('newFarm.help.alert'))}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Info Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginTop: '32px'
      }}>
        <div style={{
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            💡 {t('newFarm.card.nextSteps.title')}
          </h4>
          <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>
            {t(isProcessingPlant ? 'newFarm.card.nextSteps.bodyProcessingPlant' : 'newFarm.card.nextSteps.bodyFarm')}
          </p>
        </div>

        <div style={{
          padding: '16px',
          background: '#f0fdf4',
          borderRadius: '12px',
          border: '1px solid #bbf7d0'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
            🔒 {t('newFarm.card.security.title')}
          </h4>
          <p style={{ fontSize: '13px', color: '#166534', lineHeight: '1.5' }}>
            {t('newFarm.card.security.body')}
          </p>
        </div>
      </div>
    </div>
  )
}
